/**
 * Normalise a product image-gallery value into a real `string[]`.
 *
 * The API's `imageUrls` can arrive in more than one shape depending on how the
 * row was stored over the app's history:
 *   • a real array                ["/v1/files/a.png", "/v1/files/b.png"]
 *   • a JSON-encoded array string "[\"/v1/files/a.png\"]"        ← double-encoded
 *   • a doubly-encoded string     "\"[\\\"/v1/files/a.png\\\"]\""
 *   • a single bare URL string    "/v1/files/a.png"
 *   • null / undefined / ""       → []
 *
 * Always returns a plain array (never null) so callers can safely `.map`,
 * `.length`, `[0]`, `.forEach` without re-checking the shape. Blanks are dropped.
 */
export function toImageUrls(value: unknown): string[] {
  return collectUrls(value);
}

function collectUrls(value: unknown, depth = 0): string[] {
  if (value == null || depth > 6) return [];
  if (Array.isArray(value)) return value.flatMap((v) => collectUrls(v, depth + 1));
  if (typeof value !== 'string') return [];

  const s = value.trim();
  if (!s) return [];

  // Only try to JSON-decode strings that look encoded; a bare URL ("/v1/files/…")
  // is taken verbatim.
  if (s[0] === '[' || s[0] === '"') {
    try {
      const parsed = JSON.parse(s);
      if (parsed !== s) return collectUrls(parsed, depth + 1);
    } catch {
      /* not valid JSON — treat as a plain URL */
    }
  }
  return [s];
}

/**
 * The one image to show for a product ROW — its first usable gallery image, else the legacy single
 * `imageUrl`, else undefined so the caller falls back to initials.
 *
 * Three pages had grown their own copy of this line (`firstImg`), each reaching into `imageUrls`
 * slightly differently. A product's thumbnail is now the same picture wherever it is drawn.
 */
export function productImage(p: any): string | undefined {
  return productImages(p)[0];
}

/**
 * The product's WHOLE gallery, in order — for anywhere the picture can be opened rather than only
 * glanced at. `productImage` is this list's first entry, so a thumbnail and the preview it opens can
 * never disagree about which photo comes first.
 */
export function productImages(p: any): string[] {
  const gallery = toImageUrls(p?.imageUrls);
  return gallery.length ? gallery : toImageUrls(p?.imageUrl);
}
